import React from 'react'
import { i18n } from '../i18n/i18n'
import { LabelApprovalData } from '../apis/labelApproval.api'
import Card from '../components/Card'
import Modal from '../components/Modal'

interface Props {
    isOpen: boolean
    labelName?: string
    approvals: LabelApprovalData[]
    onClose: () => void
}

const statusClass: Record<string, string> = {
    approved: 'text-green-600',
    staleApproved: 'text-orange-500',
    rejected: 'text-orange-500',
    pending: 'text-amber-500',
    expired: 'text-secondary',
    cancelled: 'text-secondary'
}

const statusText = (approval: LabelApprovalData) => {
    if (approval.status === 'approved') {
        return approval.isStale
            ? i18n('Re-approval needed: the design has changed!')
            : i18n('Approved')
    }
    if (approval.status === 'rejected') return i18n('Not approved')
    if (approval.status === 'pending') return i18n('Approval pending')
    if (approval.status === 'expired') return i18n('Expired')
    return i18n('Cancelled')
}

const formatDate = (value?: string | null) => {
    if (!value) return ''
    return new Date(value).toLocaleString('en-GB', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        hour12: true
    })
}

/**
 * The audit trail: every approval request ever sent for this label, newest
 * first, with the approver's answer and remarks.
 */
const LabelApprovalHistory: React.FC<Props> = ({
    isOpen, labelName, approvals, onClose
}) => (
    <Modal
        title={i18n('Approval history')}
        isOpen={isOpen}
        onClose={onClose}
        className="max-w-3xl">
        <Card>
            {labelName && (
                <p className="text-sm text-secondary mb-3">
                    {i18n('Label')}: <strong>{labelName}</strong>
                </p>
            )}

            {approvals.length === 0 && (
                <p className="text-secondary">
                    {i18n('This label has not been sent for approval yet.')}
                </p>
            )}

            {approvals.map(approval => (
                <div
                    key={approval.id}
                    className="border-b border-solid border-gray-200 py-3 last:border-b-0">
                    <div className="flex justify-between items-start gap-4">
                        <div>
                            <p className={`font-medium ${statusClass[approval.status === 'approved' && approval.isStale ? 'staleApproved' : approval.status] ?? ''}`}>
                                {statusText(approval)}
                            </p>
                            <p className="text-sm text-secondary">
                                {approval.approverName} &lt;{approval.approverEmail}&gt;
                            </p>
                        </div>
                        <div className="text-right text-xs text-secondary whitespace-nowrap">
                            <p>{i18n('Sent')}: {formatDate(approval.requestedAt)}</p>
                            {approval.respondedAt && (
                                <p>{i18n('Answered')}: {formatDate(approval.respondedAt)}</p>
                            )}
                        </div>
                    </div>

                    {approval.message && (
                        <p className="text-sm mt-2">
                            <span className="text-secondary">{i18n('Message')}: </span>
                            {approval.message}
                        </p>
                    )}

                    {approval.responderRemarks && (
                        <p className="text-sm mt-1">
                            <span className="text-secondary">{i18n('Remarks')}: </span>
                            {approval.responderRemarks}
                        </p>
                    )}
                </div>
            ))}
        </Card>
    </Modal>
)

export default LabelApprovalHistory
